<template>
  <section class="board-conversations">
    <upcoming-scheduled-messages-filter
      :paginated-list="paginatedList"
      :available-filters-data="availableFiltersData"
      :disabled="isLoading"
      :initial-filters="filters"
      :is-agent-filter-locked="isRestrictedUser"
      :restricted-agent-id="currentUser.id"
      :restricted-agent-name="currentUser.name || currentUser.available_name"
      @filter-applied="onFilterApplied"
    />

    <div class="table-container">
      <ve-table
        :columns="columns"
        :table-data="paginatedList"
        :fixed-header="true"
        :border-around="true"
      />

      <div v-if="isLoading" class="loading-container">
        <span class="loader"></span>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.LOADING_MESSAGE_TABLE') }}
      </div>

      <div
        v-if="!isLoading && hasFetchedOnce && paginatedList.length === 0"
        class="empty-state"
      >
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.EMPTY_STATE') }}
      </div>

      <div class="table-bottom-spacer"></div>
    </div>

    <div class="pagination-container">
      <TableFooter
        :currentPage="currentPage"
        :pageSize="itemsPerPage"
        :totalCount="count"
        @page-change="onPageChange"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import UpcomingScheduledMessagesAPI from '../../../../api/upcomingScheduledMessages';
import UpcomingScheduledMessagesFilter from './UpcomingScheduledMessagesFilter';
import agentMixin from 'dashboard/mixins/agentMixin';

export default {
  components: {
    VeTable,
    TableFooter,
    UpcomingScheduledMessagesFilter,
  },
  mixins: [agentMixin],
  data() {
    return {
      count: 0,
      paginatedList: [],
      itemsPerPage: 15,
      currentPage: 1,
      refreshInterval: null,
      isLoading: false,
      hasFetchedOnce: false,
      filters: {
        inboxId: null,
        teamId: null,
        agentId: null,
        enableCancellation: null,
        conversationStatus: null,
        startDate: null,
        endDate: null,
      },
      availableFiltersData: {
        inboxes: [],
        teams: [],
        users: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    isRestrictedUser() {
      return ![
        'SuperAdmin',
        'administrator',
        'supervisor',
        'team_supervisor',
      ].includes(this.currentUser.type || this.currentRole);
    },
    columns() {
      return [
        {
          field: 'scheduledAt',
          key: 'scheduledAt',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.SCHEDULED_AT'
          ),
          renderBodyCell: ({ row }) => (
            <router-link
              to={`/app/accounts/${this.$route.params.accountId}/conversations/${row.conversationId}`}
              class="scheduled-at-link"
            >
              {row.scheduledAt}
            </router-link>
          ),
        },
        {
          field: 'contactName',
          key: 'contactName',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.CONTACT'
          ),
          renderBodyCell: ({ row }) => {
            if (!this.canViewContact()) {
              return <span>{row.contactName}</span>;
            }

            return (
              <router-link
                to={`/app/accounts/${this.$route.params.accountId}/contacts/${row.contactId}`}
                class="contact-name-link"
              >
                {row.contactName}
              </router-link>
            );
          },
        },
        {
          field: 'phone',
          key: 'phone',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'
          ),
          renderBodyCell: ({ row }) => {
            if (!this.canViewPhone()) {
              return <span>---</span>;
            }

            return <span>{row.phone}</span>;
          },
        },
        {
          field: 'team',
          key: 'team',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.ASSIGNED_TEAM'
          ),
        },
        {
          field: 'assignee',
          key: 'assignee',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.ASSIGNED_AGENT'
          ),
        },
        {
          field: 'enableCancellation',
          key: 'enableCancellation',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.AUTO_CANCELLATION'
          ),
          renderBodyCell: ({ row }) => {
            const isYes = row.enableCancellation;

            return (
              <span class={isYes ? 'badge badge-green' : 'badge badge-red'}>
                {isYes
                  ? this.$t(
                      'UPCOMING_SCHEDULED_MESSAGES_PAGE.AUTO_CANCELLATION.YES'
                    )
                  : this.$t(
                      'UPCOMING_SCHEDULED_MESSAGES_PAGE.AUTO_CANCELLATION.NO'
                    )}
              </span>
            );
          },
        },
        {
          field: 'content',
          key: 'content',
          title: this.$t(
            'UPCOMING_SCHEDULED_MESSAGES_PAGE.LIST.TABLE_HEADER.MESSAGE'
          ),
          renderBodyCell: ({ row }) => {
            return (
              <span class="message-cell" title={row.content}>
                {row.content}
              </span>
            );
          },
        },
      ];
    },
    requestParams() {
      return {
        inbox_id: this.filters.inboxId,
        team_id: this.filters.teamId,
        agent_id: this.filters.agentId,
        enable_cancellation: this.filters.enableCancellation,
        conversation_status: this.filters.conversationStatus,
        start_date: this.filters.startDate,
        end_date: this.filters.endDate,
        page: this.currentPage,
        per_page: this.itemsPerPage,
      };
    },
    isPrivilegedRole() {
      return [
        'SuperAdmin',
        'administrator',
        'supervisor',
        'team_supervisor',
      ].includes(this.currentUser.type || this.currentRole);
    },
  },
  async mounted() {
    if (this.isRestrictedUser) {
      this.filters.agentId = this.currentUser.id;
    }

    await this.loadFilterOptions();

    this.resetAndFetchData();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    onFilterApplied(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.resetAndFetchData();
    },
    canViewContact() {
      return this.isPrivilegedRole || this.agentRules?.contact !== false;
    },
    canViewPhone() {
      return this.isPrivilegedRole || this.agentRules?.mask_number !== false;
    },
    async resetAndFetchData() {
      this.currentPage = 1;
      await this.fetchScheduledMessages();
    },
    async fetchScheduledMessages() {
      this.isLoading = true;

      try {
        const {
          data: { payload, meta },
        } = await UpcomingScheduledMessagesAPI.getWithParams(
          this.requestParams
        );

        this.count = meta?.count || payload.length;

        this.paginatedList = payload.map(item => ({
          id: item.id,
          contactId: item.contact?.id,
          contactName: item.contact?.name || '---',
          phone: item.contact?.phone_number || '---',
          team: item.team?.name || '---',
          assignee: item.assignee?.name || '---',
          scheduledAt: this.formatDate(item.scheduled_at),
          conversationId: item.conversation_id,
          enableCancellation: item.enable_cancellation,
          content: item.content || '',
          raw: item,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
        this.hasFetchedOnce = true;
      }
    },
    formatDate(date) {
      if (!date) return '---';

      return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchScheduledMessages();
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.fetchScheduledMessages();
      }, 300000);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async loadFilterOptions() {
      await this.$store.dispatch('agents/get');
      await this.$store.dispatch('inboxes/get');
      await this.$store.dispatch('teams/get');

      this.availableFiltersData = {
        inboxes: this.$store.getters['inboxes/getInboxes'],
        teams: this.$store.getters['teams/getTeams'],
        users: this.$store.getters['agents/getAgents'],
      };
    },
  },
};
</script>

<style scoped lang="scss">
.board-conversations::v-deep {
  .ve-table {
    thead.ve-table-header .ve-table-header-tr .ve-table-header-th {
      font-size: var(--font-size-mini);
      padding: var(--space-small) var(--space-two);
    }

    tbody.ve-table-body .ve-table-body-tr .ve-table-body-td {
      padding: var(--space-slab) var(--space-two);
    }
  }
}

.table-container {
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  overflow-x: auto;
  padding-bottom: 60px;
  flex-grow: 1;
}

.pagination-container {
  position: sticky;
  bottom: 0;
  padding: var(--space-slab);
  z-index: 10;
  background-color: var(--white);
}

.board-conversations {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-medium);
  font-weight: bold;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: bold;
  display: inline-block;
  text-transform: lowercase;
}

.badge-green {
  background: #2ecc71;
  color: white;
}

.badge-red {
  background: #e74c3c;
  color: white;
}

.message-cell {
  display: -webkit-box;
  -webkit-box-orient: vertical;

  -webkit-line-clamp: 3;

  overflow: hidden;

  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;

  line-height: 1.4;
  max-width: 420px;
}

.table-bottom-spacer {
  height: 100px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  font-size: 14px;
  color: #777;
}
</style>
